import { BACKEND_URL } from '@meno/ui/lib/api'

// Client-side video-thumbnail cache shared with PlaylistDetailsPage. Videos
// have no server thumbnail, so the detail page captures a frame and stashes a
// data-URL here keyed by media id; the cards reuse it when available.
const THUMB_CACHE_PREFIX = 'meno_vthumb_'

type ThumbMedia = { id: number; file_type: string; url: string }

function resolveUrl(url: string): string {
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`
}

// Derive a playlist's thumbnail from its content. We walk the media in
// playlist order and return the first renderable preview — an image's URL, or
// a video's cached frame if one was captured. Returns null when nothing is
// renderable (empty playlist, or only videos/PDFs with no cached frame), and
// the caller falls back to the typed placeholder icon. Because it reads the
// live `media` array, the thumbnail updates automatically whenever the
// playlist's content changes.
export function playlistThumbUrl(media?: ThumbMedia[] | null): string | null {
  if (!media || media.length === 0) return null
  for (const m of media) {
    if (m.file_type.startsWith('image')) return resolveUrl(m.url)
    if (m.file_type.startsWith('video')) {
      try {
        const cached = localStorage.getItem(`${THUMB_CACHE_PREFIX}${m.id}`)
        if (cached) return cached
      } catch {
        /* localStorage unavailable — fall through to the next item */
      }
    }
  }
  return null
}

// Layout thumbnail — same logic as playlistThumbUrl, but a layout's content
// lives across zones (each holding `media_ids`) rather than a flat media list.
// We walk the zones in order, resolve each media id against the catalogue, and
// return the first renderable preview (image URL or cached video frame). The
// Content Hub passes its already-loaded media catalogue so the card reuses one
// fetch. Returns null when nothing is renderable, so the LayoutCard falls back
// to the zone-arrangement mockup.
export function layoutThumbUrl(
  zones: Array<{ media_ids?: number[] }> | undefined | null,
  media: ThumbMedia[],
): string | null {
  if (!zones || zones.length === 0) return null
  const byId = new Map<number, ThumbMedia>(media.map(m => [m.id, m]))
  for (const z of zones) {
    for (const mid of z.media_ids ?? []) {
      const m = byId.get(mid)
      if (!m) continue
      if (m.file_type.startsWith('image')) return resolveUrl(m.url)
      if (m.file_type.startsWith('video')) {
        try {
          const cached = localStorage.getItem(`${THUMB_CACHE_PREFIX}${m.id}`)
          if (cached) return cached
        } catch {
          /* localStorage unavailable — fall through to the next item */
        }
      }
    }
  }
  return null
}
